// Auth0 authorization-code + PKCE, hand-rolled rather than via @auth0/auth0-spa-js.
//
// The SDK assumes a single-page app that owns the window: it redirects the whole
// page and reads its result back from its own localStorage cache. The add-in's
// login happens in a short-lived Office dialog on a different window, whose only
// channel back to the task pane is one `messageParent` string — so we need the raw
// tokens in hand, not a cache the dialog takes to the grave. Matching the Cognito
// flow next door also keeps both paths readable side by side.
import { config } from '../config';
const REDIRECT_URI = `${config.addinOrigin}/authdialog.html`;
const PKCE_KEY = 'tl.auth0.pkce';
const b64url = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
/**
 * Sends the dialog to Auth0. `connection` skips the provider chooser and goes
 * straight to the tenant's IdP — the same connection names dealroom-fe uses
 * ('wtlavs' for Wincasa, 'tlsaml' for the top.legal test tenant).
 */
export async function startAuth0Login(connection, loginHint) {
    const verifier = b64url(crypto.getRandomValues(new Uint8Array(32)).buffer);
    // sessionStorage, not localStorage: the verifier is single-use and scoped to this
    // dialog window, and must not outlive it for another login to pick up.
    sessionStorage.setItem(PKCE_KEY, verifier);
    const challenge = b64url(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)));
    const url = new URL(`https://${config.auth0.domain}/authorize`);
    url.search = new URLSearchParams({
        response_type: 'code',
        client_id: config.auth0.clientId,
        redirect_uri: REDIRECT_URI,
        // offline_access buys a refresh token, which is what spares the user a dialog
        // every hour — far more valuable in Outlook than in a browser tab.
        scope: 'openid profile email offline_access',
        connection,
        code_challenge: challenge,
        code_challenge_method: 'S256',
        ...(loginHint ? { login_hint: loginHint } : {}),
    }).toString();
    window.location.replace(url.toString());
}
/** Exchanges the returned code for tokens. Runs in the dialog, after Auth0 redirects back. */
export async function finishAuth0Login(code) {
    const verifier = sessionStorage.getItem(PKCE_KEY);
    if (!verifier)
        throw new Error('Login session expired, please try again');
    sessionStorage.removeItem(PKCE_KEY);
    const res = await fetch(`https://${config.auth0.domain}/oauth/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
            grant_type: 'authorization_code',
            client_id: config.auth0.clientId,
            code,
            redirect_uri: REDIRECT_URI,
            code_verifier: verifier,
        }),
    });
    const json = await res.json();
    if (!res.ok)
        throw new Error(json?.error_description || json?.error || 'Auth0 token exchange failed');
    // The BE authorizer verifies the ID token, exactly as dealroom-fe's
    // getAccessToken() returns `response.id_token` rather than the access token.
    return { idToken: json.id_token, refreshToken: json.refresh_token, expiresIn: json.expires_in };
}
/** Silent renewal from the task pane — no dialog, so the user is never interrupted. */
export async function refreshAuth0(refreshToken) {
    const res = await fetch(`https://${config.auth0.domain}/oauth/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
            grant_type: 'refresh_token',
            client_id: config.auth0.clientId,
            refresh_token: refreshToken,
        }),
    });
    const json = await res.json();
    if (!res.ok)
        throw new Error(json?.error_description || json?.error || 'Auth0 refresh failed');
    // Rotation is on for SPA clients, so a new refresh token may come back; the caller
    // must store it, or the next renewal fails with the spent one.
    return { idToken: json.id_token, refreshToken: json.refresh_token, expiresIn: json.expires_in };
}
